// src/Components/Create/Create.js
import React, { Fragment, useEffect, useState, useContext, useRef, useCallback } from "react";
import Header from "../Header/Header";
import { AuthContext } from "../../contextStore/AuthContext";
import GoLoading from "../Loading/GoLoading";
import client from "../../api/client";
import { useNavigate } from "react-router-dom";
import SellerPlansModal from "../Plans/SellerPlansModal";
import "./Create.css";
delete client.defaults.headers["Content-Type"];

const MAX_FILES = 8;

const CONTACT_PATTERNS = [
  /\b\d{10}\b/,
  /\b\d{5,15}\b/,
  /\b@[\w]+\b/,
  /\b(?:https?:\/\/)?(?:www\.)?[\w-]+\.[a-z]{2,}\b/,
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/,
  /\b(?:contact me|contact us|call me|whatsapp|telegram|dm|direct message)\b/i,
];

const STEPS = [
  { id: 'basic', title: 'Basic Info', subtitle: 'Tell us about your machine' },
  { id: 'details', title: 'Details', subtitle: 'Add specifications and description' },
  { id: 'location', title: 'Location', subtitle: 'Where is the machine located?' },
  { id: 'contact', title: 'Contact', subtitle: 'How can buyers reach you?' },
  { id: 'photos', title: 'Photos', subtitle: 'Add images of your machine' },
  { id: 'review', title: 'Review', subtitle: 'Check your listing details' }
];

export default function Create() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // localStorage utility functions
  const getPendingMachineId = useCallback(() => {
    try {
      return localStorage.getItem('pending_machine_id');
    } catch {
      return null;
    }
  }, []);

  const clearPendingMachine = useCallback(() => {
    try {
      localStorage.removeItem('pending_machine_id');
    } catch { }
  }, []);

  const setPendingMachineId = useCallback((id) => {
    try {
      localStorage.setItem('pending_machine_id', id);
    } catch { }
  }, []);

  // Check for pending machine on component mount
  useEffect(() => {
    const checkPendingMachine = async () => {
      const pendingId = getPendingMachineId();
      if (pendingId) {
        try {
          const checkRes = await client.get(`/api/machines/${pendingId}`);
          // If machine is already published, clear the pending ID
          if (checkRes.data?.machine?.status === 'published') {
            clearPendingMachine();
          }
        } catch {
          // Machine doesn't exist or can't be accessed
          clearPendingMachine();
        }
      }
    };
    checkPendingMachine();
  }, [getPendingMachineId, clearPendingMachine]);

  const [currentStep, setCurrentStep] = useState(0);
  const [industryOptions, setIndustryOptions] = useState([]);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await client.get("/api/categories");
        if (res.data?.ok && Array.isArray(res.data.categories)) {
          setIndustryOptions(res.data.categories.map((c) => c.name));
        }
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    }
    fetchCategories();
  }, []);

  // form fields
  const [title, setTitle] = useState("");
  const [industries, setIndustries] = useState([]);
  const [manuYear, setManuYear] = useState("");
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [geoSuggestions, setGeoSuggestions] = useState([]);
  const [selectedGeo, setSelectedGeo] = useState({ lat: null, lon: null, label: null, postcode: null });
  const [pincode, setPincode] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [images, setImages] = useState([]);
  const [hasContactInfo, setHasContactInfo] = useState(false);
  const [manufacturingBy, setManufacturingBy] = useState("");
  const [production, setProduction] = useState("");

  // New states for validation
  const [hasNumbers, setHasNumbers] = useState(false);
  const [hasSpecialChars, setHasSpecialChars] = useState(false);
  const [validationWarning, setValidationWarning] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  const geoTimerRef = useRef(null);
  const latestQueryRef = useRef("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const imagesRef = useRef([]);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => { imagesRef.current = images; }, [images]);
  useEffect(() => () => { (imagesRef.current || []).forEach(e => e.url && URL.revokeObjectURL(e.url)); }, []);

  // Enhanced useEffect for validation (contact info, numbers, and special characters)
  useEffect(() => {
    const txt = (description || "").trim();

    if (!txt) {
      setHasContactInfo(false);
      setHasNumbers(false);
      setHasSpecialChars(false);
      setValidationWarning("");
      return;
    }

    // Check for contact info
    const hasContact = CONTACT_PATTERNS.some((pattern) => {
      const re = new RegExp(pattern.source, pattern.flags);
      return re.test(txt);
    });
    setHasContactInfo(hasContact);

    // Check for numbers
    const numberRegex = /\d/;
    const hasNums = numberRegex.test(txt);
    setHasNumbers(hasNums);

    // Check for special characters (excluding spaces, basic punctuation: . , ! ? - )
    const specialCharRegex = /[~`!@#$%^&*()_+={}\[\]|\\:;"'<>\/]/;
    const hasSpecial = specialCharRegex.test(txt);
    setHasSpecialChars(hasSpecial);

    // Set appropriate warning message
    if (hasNums && hasSpecial) {
      setValidationWarning("Numbers and special characters (@, #, _, etc.) are not allowed in the description. Please use only letters and basic punctuation (. , ! ? -).");
    } else if (hasNums) {
      setValidationWarning("Numbers are not allowed in the description. Please remove all numbers.");
    } else if (hasSpecial) {
      setValidationWarning("Special characters (@, #, _, etc.) are not allowed. Please use only letters and basic punctuation (. , ! ? -).");
    } else {
      setValidationWarning("");
    }
  }, [description]);

  // Step navigation
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
  };

  // Validation for each step
  const validateStep = (step) => {
    switch (step) {
      case 0: // Basic Info
        if (!title.trim()) {
          setError("Machine name is required.");
          return false;
        }
        if (!price || Number.isNaN(Number(price)) || parseFloat(price) <= 0) {
          setError("Please enter a valid price.");
          return false;
        }
        return true;

      case 1: // Details
        if (hasContactInfo) {
          setError("Please remove contact information from the description.");
          return false;
        }
        if (hasNumbers || hasSpecialChars) {
          setError("Please remove numbers and special characters from the description before proceeding.");
          return false;
        }
        return true;

      case 2: // Location
        if (!cityInput.trim()) {
          setError("Please enter a city location.");
          return false;
        }
        return true;

      case 3: // Contact
        if (!contactNumber || String(contactNumber).length < 10) {
          setError("Please provide a valid 10-digit contact number.");
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    setError("");
    if (validateStep(currentStep)) {
      nextStep();
    }
  };

  const fetchGeoSuggestions = async (q) => {
    const raw = (q || "").trim();
    if (!raw) {
      latestQueryRef.current = "";
      setGeoSuggestions([]);
      return;
    }

    const queryId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    latestQueryRef.current = queryId;

    try {
      const res = await client.get("/api/geo/search", { params: { q: raw, limit: 12 }, timeout: 8000 });

      let results = [];
      if (!res || !res.data) results = [];
      else if (Array.isArray(res.data)) results = res.data;
      else if (Array.isArray(res.data.results)) results = res.data.results;
      else if (Array.isArray(res.data.items)) results = res.data.items;
      else results = [];

      if (latestQueryRef.current !== queryId) {
        return;
      }

      const qlc = raw.toLowerCase();
      const scored = results
        .map((it) => {
          const dn = ((it.display_name || it.name) || "").toLowerCase();
          const tokens = dn.split(/[,\s]+/).filter(Boolean);
          let score = 0;
          if (tokens[0] && tokens[0].startsWith(qlc)) score = 3;
          else if (tokens.some((t) => t.startsWith(qlc))) score = 2;
          else if (dn.includes(qlc)) score = 1;
          return { ...it, _score: score };
        })
        .sort((a, b) => b._score - a._score);

      setGeoSuggestions(scored.slice(0, 8));
      setHighlightIndex(-1);
    } catch (err) {
      console.warn("geo search error", err && (err.message || err));
      if (latestQueryRef.current) {
        setGeoSuggestions([]);
      }
    }
  };

  const onCityChange = (e) => {
    const v = e.target.value;
    setCityInput(v);
    setSelectedGeo({ lat: null, lon: null, label: null, postcode: null });
    setPincode("");

    if (geoTimerRef.current) clearTimeout(geoTimerRef.current);

    const delay = v.length <= 2 ? 140 : 260;
    geoTimerRef.current = setTimeout(() => {
      fetchGeoSuggestions(v);
    }, delay);
  };

  const onSelectSuggestion = (s) => {
    setCityInput(s.display_name || s.name || "");
    setSelectedGeo({
      lat: s.lat,
      lon: s.lon,
      label: s.display_name || s.name || "",
      postcode: s.postcode || (s.address && s.address.postcode) || null,
    });
    setGeoSuggestions([]);
    setHighlightIndex(-1);
    if (s.postcode || (s.address && s.address.postcode)) {
      const code = String(s.postcode || (s.address && s.address.postcode) || "").match(/\d{3,8}/);
      setPincode(code ? code[0] : "");
    }
  };

  const onCityBlur = () => setTimeout(() => setGeoSuggestions([]), 140);
  const onCityKeyDown = (e) => {
    if (!geoSuggestions || geoSuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex(i => Math.min(geoSuggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex(i => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (highlightIndex >= 0 && highlightIndex < geoSuggestions.length) {
        e.preventDefault();
        onSelectSuggestion(geoSuggestions[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setGeoSuggestions([]);
      setHighlightIndex(-1);
    }
  };

  // Image functions
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragActive(true); }
  function handleDragLeave(e) { e.preventDefault(); e.stopPropagation(); setDragActive(false); }
  function handleDrop(e) { e.preventDefault(); e.stopPropagation(); setDragActive(false); const dropped = Array.from(e.dataTransfer.files || []); if (!dropped.length) return; addFiles(dropped); }
  function createFileEntry(file) { return { file, url: URL.createObjectURL(file) }; }
  function addFiles(list) {
    if (!list || !list.length) return;
    const allowed = list.filter((f) => f.type && f.type.startsWith("image/"));
    if (!allowed.length) return;
    const newEntries = allowed.map(createFileEntry);
    setImages((prev) => {
      const combined = [...prev, ...newEntries].slice(0, MAX_FILES);
      if (combined.length < prev.length + newEntries.length) {
        const keepCount = Math.max(0, MAX_FILES - prev.length);
        newEntries.slice(keepCount).forEach((e) => e.url && URL.revokeObjectURL(e.url));
      }
      return combined;
    });
  }
  function handleFileInputChange(e) { const files = Array.from(e.target.files || []); addFiles(files); e.target.value = ""; }
  function openGalleryChooser() { galleryInputRef.current && galleryInputRef.current.click(); }
  function openCameraCapture() { cameraInputRef.current && cameraInputRef.current.click(); }
  function removeImage(idx) { setImages(prev => { const toRemove = prev[idx]; if (toRemove && toRemove.url) try { URL.revokeObjectURL(toRemove.url); } catch (err) { } return prev.filter((_, i) => i !== idx); }); }
  function toggleIndustry(val) {
    setIndustries((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));
  }

  // Final validation and submission
  const validateForm = () => {
    if (!user) { setError("Please login to post a machine."); setTimeout(() => navigate("/login"), 1200); return false; }
    if (!title.trim()) { setError("Machine name is required."); return false; }
    if (!price || Number.isNaN(Number(price)) || parseFloat(price) <= 0) { setError("Please enter a valid price."); return false; }
    if (manuYear && (Number(manuYear) < 1900 || Number(manuYear) > 2100)) { setError("Manufacturing year must be between 1900 and 2100."); return false; }
    if (hasContactInfo) { setError("Please remove contact information from the description."); return false; }
    if (hasNumbers || hasSpecialChars) { setError("Please remove numbers and special characters from the description."); return false; }
    if (!contactNumber || String(contactNumber).length < 10) { setError("Please provide a valid 10-digit contact number."); return false; }
    return true;
  };

  // Cleanup helper function
  const cleanupAfterCreate = () => {
    try {
      (imagesRef.current || []).forEach(
        (e) => e.url && URL.revokeObjectURL(e.url)
      );
    } catch { }

    setImages([]);
    setPendingFormData(null);
    setTitle("");
    setDescription("");
    setPrice("");
    setCityInput("");
    setSelectedGeo({ lat: null, lon: null, label: null, postcode: null });
    setPincode("");
    clearPendingMachine();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      // 🔥 CHECK IF WE ALREADY HAVE A PENDING MACHINE
      const existingMachineId = getPendingMachineId();

      if (existingMachineId) {
        // Check if this machine still exists and is still a draft
        try {
          const checkRes = await client.get(`/api/machines/${existingMachineId}`);
          if (checkRes.data?.machine?.status === 'pending') {
            // Use existing draft machine
            const snapshot = {
              title: title.trim(),
              description: description.trim(),
              specification: description.trim(),
              price: parseFloat(price),
              currency: "INR",
              location_city: cityInput.trim(),
              pincode: pincode,
              manu_year: manuYear,
              model: model.trim(),
              contact_number: contactNumber.trim(),
              industries: JSON.stringify(industries),
              imagesFiles: images.map((entry) => entry.file),
              latitude: selectedGeo.lat || null,
              longitude: selectedGeo.lon || null,
              location_label: selectedGeo.label || (cityInput.trim() || null),
              manufacturing_by: manufacturingBy.trim(),
              production: production.trim(),
            };

            setPendingFormData({
              ...snapshot,
              machineId: existingMachineId,
            });

            setPlansOpen(true);
            setLoading(false);
            return;
          }
        } catch {
          // Machine doesn't exist or can't be accessed - continue to create new
        }
      }

      // 1️⃣ Prepare snapshot (same as before)
      const snapshot = {
        title: title.trim(),
        description: description.trim(),
        specification: description.trim(),
        price: parseFloat(price),
        currency: "INR",
        location_city: cityInput.trim(),
        pincode: pincode,
        manu_year: manuYear,
        model: model.trim(),
        contact_number: contactNumber.trim(),
        industries: JSON.stringify(industries),
        imagesFiles: images.map((entry) => entry.file),
        latitude: selectedGeo.lat || null,
        longitude: selectedGeo.lon || null,
        location_label: selectedGeo.label || (cityInput.trim() || null),
        manufacturing_by: manufacturingBy.trim(),
        production: production.trim(),
      };

      if (!snapshot.pincode && selectedGeo.postcode) {
        snapshot.pincode = selectedGeo.postcode;
      }

      // 2️⃣ Create machine FIRST (status = pending)
      const form = new FormData();
      Object.entries(snapshot).forEach(([key, value]) => {
        if (key === "imagesFiles") return;
        if (value !== undefined && value !== null) {
          form.append(key, value);
        }
      });

      snapshot.imagesFiles.forEach((file) => {
        form.append("images", file);
      });

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await client.post("/api/machines", form, {
        headers,
        timeout: 20000,
      });
      console.log('🔍 Machine creation API response:', res.data); // ADD THIS!


      if (!res?.data?.id) {
        console.error('❌ Machine creation failed - no ID in response:', res.data);
        throw new Error("Failed to create machine listing");
      }

      const machineId = res.data.id;
      console.log('🔍 New machine created with ID:', machineId); // ADD THIS!

      // 🔥 SAVE THE MACHINE ID FOR RETRY
      setPendingMachineId(machineId);

      // 3️⃣ Store machineId and open payment modal
      setPendingFormData({
        ...snapshot,
        machineId,
      });

      // 🔥 FREE LISTING FLOW
      if (res.data?.published === true || res.data?.free_listing === true) {
        clearPendingMachine(); // 🔥 CLEAR WHEN SUCCESS
        navigate(`/machines/${machineId}`);
        cleanupAfterCreate();
        return;
      }

      // Paid listing → open plans modal
      setPlansOpen(true);

    } catch (err) {
      console.error("Create machine error:", err);
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to create machine"
      );
    } finally {
      setLoading(false);
    }
  };

  async function handleOnPurchased({
    type,
    planId,
    paymentId,
    paymentFailed = false,
  }) {
    setPlansOpen(false);
    setError("");
    setSuccess("");

    // ❌ Payment failed / cancelled
    if (paymentFailed || !paymentId) {
      setError("Payment was cancelled or failed. Please retry.");
      return; // Don't clear - keep the draft for retry
    }

    // 🔒 Machine MUST already exist (created before payment)
    const machineId = pendingFormData?.machineId;

    if (!machineId) {
      setError("Machine not found. Please try again.");
      return;
    }

    setPublishing(true);
    setLoading(true);

    try {
      // ✅ Webhook already handles publishing
      // Frontend just waits a bit and redirects
      setSuccess("Payment successful! Publishing your listing...");

      // ⏳ Small delay to allow webhook to complete
      const redirectWithRetry = async () => {
        for (let i = 0; i < 5; i++) {
          try {
            const check = await client.get(`/api/machines/${machineId}`);

            // ✅ CORRECT CHECK
            if (check?.data?.machine?.status === "published") {
              navigate(`/machines/${machineId}`);
              cleanupAfterCreate();
              return;
            }
          } catch { }
          await new Promise(r => setTimeout(r, 1000));
        }

        // ❌ FAILED TO POST — NO DRAFT
        setError(
          "Failed to post machine. Payment was successful, but listing could not be published."
        );
        cleanupAfterCreate();
      };

      redirectWithRetry();

    } catch (err) {
      console.error("handleOnPurchased error", err);
      setError("Payment completed, but something went wrong. Please refresh.");
    } finally {
      setPublishing(false);
      setLoading(false);
    }
  }

  function handlePlansClose() { setPlansOpen(false); }

  const disablePost = loading || publishing || hasContactInfo || hasNumbers || hasSpecialChars || !user;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Info
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Basic Information</h2>
              <p className="step-subtitle">Tell us about your machine</p>
            </div>

            <div className="field">
              <label className="field-label">Machine Name *</label>
              <input
                className="input"
                placeholder="e.g. Kubota Harvester H-200, JCB Excavator 3DX"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
              <div className="muted">Be specific about make and model</div>
            </div>

            <div className="field">
              <label className="field-label">Industries</label>
              <div className="chips" role="list">
                {industryOptions.map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    className={`chip ${industries.includes(opt) ? "selected" : ""}`}
                    onClick={() => toggleIndustry(opt)}
                    aria-pressed={industries.includes(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              <div className="muted">Select relevant industries for better visibility</div>
            </div>

            <div className="row-2" style={{ marginTop: 12 }}>
              <div className="field">
                <label className="field-label">Manufacturing Year</label>
                <input
                  className="input"
                  type="number"
                  min="1900"
                  max="2100"
                  value={manuYear}
                  onChange={(e) => setManuYear(e.target.value)}
                  placeholder="YYYY"
                />
              </div>

              <div className="field">
                <label className="field-label">Manufactured By</label>
                <input
                  className="input"
                  type="text"
                  value={manufacturingBy}
                  onChange={(e) => setManufacturingBy(e.target.value)}
                  placeholder="e.g. JCB, Tata, Siemens"
                />
                <div className="muted">Name of the manufacturer / company</div>
              </div>
            </div>

            <div className="row-2" style={{ marginTop: 12 }}>
              <div className="field">
                <label className="field-label">Model</label>
                <input
                  className="input"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Model name/number"
                />
              </div>

              <div className="field">
                <label className="field-label">Production</label>
                <input
                  className="input"
                  type="text"
                  value={production}
                  onChange={(e) => setProduction(e.target.value)}
                  placeholder="e.g. 50 kg/hr or 500 units/day"
                />
                <div className="muted">Production capacity or rate (free-text)</div>
              </div>
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label className="field-label">Price (INR) *</label>
              <input
                className="input"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="e.g. 250000"
                required
              />
              <div className="muted">Enter price in Indian Rupees</div>
            </div>
          </div>
        );

      case 1: // Details
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Specifications</h2>
              <p className="step-subtitle">Describe your machine in detail</p>
            </div>

            <div className="field">
              <label className="field-label">Specification / Description *</label>
              <textarea
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Include key specifications, condition, features. Use only letters and basic punctuation (. , ! ? -). No numbers or special characters allowed."
                required
              />

              {/* Contact Info Warning */}
              {hasContactInfo && (
                <div className="validation-warning">
                  Please remove contact information from description. Use the contact number field below instead.
                </div>
              )}

              {/* Numbers & Special Characters Warning */}
              {validationWarning && (
                <div className="validation-warning error">
                  ⚠️ {validationWarning}
                </div>
              )}

              <div className="muted">
                <strong>Allowed:</strong> Letters (A-Z, a-z), spaces, and basic punctuation (. , ! ? -)<br />
                <strong>Not Allowed:</strong> Numbers (0-9), special characters (@, #, _, $, %, etc.)
              </div>
            </div>
          </div>
        );

      case 2: // Location
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Location</h2>
              <p className="step-subtitle">Where is the machine located?</p>
            </div>

            <div className="field">
              <label className="field-label">City *</label>
              <div style={{ position: "relative" }}>
                <input
                  className="input"
                  value={cityInput}
                  onChange={onCityChange}
                  onBlur={onCityBlur}
                  onKeyDown={onCityKeyDown}
                  placeholder="e.g. Pune, Chennai"
                  autoComplete="off"
                  name="city"
                  required
                />
                {geoSuggestions.length > 0 && (
                  <ul className="geo-suggestions" role="listbox">
                    {geoSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onMouseDown={() => onSelectSuggestion(s)}
                        className={`geo-item ${i === highlightIndex ? 'highlight' : ''}`}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{s.display_name}</div>
                        {(s.postcode || (s.address && s.address.postcode)) && <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>Pincode: {s.postcode || (s.address && s.address.postcode)}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="field">
              <label className="field-label">Pincode</label>
              <input className="input" value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit pincode" />
            </div>
          </div>
        );

      case 3: // Contact
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Contact Information</h2>
              <p className="step-subtitle">How can buyers reach you?</p>
            </div>

            <div className="field">
              <label className="field-label">Your Contact Number *</label>
              <input className="input" value={contactNumber} onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile number" required />
              <div className="muted">This won't be shown publicly</div>
            </div>
          </div>
        );

      case 4: // Photos
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Machine Photos</h2>
              <p className="step-subtitle">Add clear images of your machine</p>
            </div>

            <div className="field">
              <div className={`dropzone ${dragActive ? "drag-active" : ""}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={openGalleryChooser} role="button" tabIndex={0}>
                <p><strong>Drag & drop images here</strong><br />or click to browse — up to {MAX_FILES} images</p>
                <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleFileInputChange} style={{ display: "none" }} />
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileInputChange} style={{ display: "none" }} />
                <div style={{ marginTop: 8, display: "flex", gap: 8, justifyContent: "center", flexWrap: 'wrap' }}>
                  <button type="button" className="nav-btn btn-prev" onClick={(ev) => { ev.stopPropagation(); openCameraCapture(); }}>Take Photo</button>
                  <button type="button" className="nav-btn btn-next" onClick={(ev) => { ev.stopPropagation(); openGalleryChooser(); }}>Choose from Gallery</button>
                </div>
              </div>

              {images.length > 0 && (
                <div className="thumb-grid">
                  {images.map((entry, i) => (
                    <div key={i} className="thumb">
                      <img src={entry.url} alt={`Preview ${i + 1}`} />
                      <div className="remove" onClick={(ev) => { ev.stopPropagation(); removeImage(i); }} title="Remove image">×</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <div className="muted">JPG, PNG, WEBP • Max 5MB each</div>
                <div className="muted">{images.length}/{MAX_FILES}</div>
              </div>
            </div>
          </div>
        );

      case 5: // Review
        return (
          <div className="form-step active">
            <div className="step-header">
              <h2 className="step-title">Review Your Listing</h2>
              <p className="step-subtitle">Check all details before posting</p>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="field">
                <strong>Machine Name:</strong> {title}
              </div>
              <div className="field">
                <strong>Price:</strong> ₹{price}
              </div>
              <div className="field">
                <strong>Model:</strong> {model || 'Not specified'}
              </div>
              <div className="field">
                <strong>Manufacturing Year:</strong> {manuYear || 'Not specified'}
              </div>
              <div className="field">
                <strong>Manufactured By:</strong> {manufacturingBy || 'Not specified'}
              </div>
              <div className="field">
                <strong>Production:</strong> {production || 'Not specified'}
              </div>
              <div className="field">
                <strong>Industries:</strong> {industries.join(', ') || 'None selected'}
              </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="field">
                <strong>Description:</strong>
                <div className="muted" style={{ marginTop: '0.5rem' }}>{description || 'No description provided'}</div>
              </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="field">
                <strong>Location:</strong> {cityInput}
              </div>
              <div className="field">
                <strong>Pincode:</strong> {pincode || 'Not specified'}
              </div>
              <div className="field">
                <strong>Contact Number:</strong> {contactNumber}
              </div>
            </div>

            <div className="card">
              <div className="field">
                <strong>Photos:</strong> {images.length} image(s) uploaded
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Fragment>
      <Header />
      {(loading || publishing) && <GoLoading />}

      <div className="create-page">
        <form onSubmit={handleSubmit} className="create-card">
          {/* Progress Bar */}
          <div className="progress-bar">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                onClick={() => index <= currentStep && goToStep(index)}
                style={{ cursor: index <= currentStep ? 'pointer' : 'default' }}
              >
                <div className="step-number">
                  {index < currentStep ? '✓' : index + 1}
                </div>
                <span className="step-title-mobile">{step.title}</span>
              </div>
            ))}
          </div>

          {/* Form Steps */}
          <div className="form-steps">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="step-navigation">
            {currentStep > 0 && (
              <button type="button" className="nav-btn btn-prev" onClick={prevStep}>
                Previous
              </button>
            )}

            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                className="nav-btn btn-next"
                onClick={handleNext}
                disabled={currentStep === 1 && (hasNumbers || hasSpecialChars || hasContactInfo)}
              >
                Next
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                <button type="submit" className="nav-btn btn-submit" disabled={disablePost}>
                  {loading ? "Preparing..." : "Post Machine Listing"}
                </button>

                {/* Add Clear Draft button */}
                {getPendingMachineId() && (
                  <button
                    type="button"
                    className="nav-btn btn-prev"
                    onClick={() => {
                      if (window.confirm("Clear your draft machine and start over?")) {
                        clearPendingMachine();
                        window.location.reload();
                      }
                    }}
                    style={{ backgroundColor: "#ff4444", color: "white" }}
                  >
                    Clear Draft & Start Over
                  </button>
                )}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {error && error.includes("Payment was cancelled") && (
            <button
              type="button"
              className="nav-btn btn-next"
              onClick={() => setPlansOpen(true)}
              style={{ marginTop: "10px" }}
            >
              Retry Payment
            </button>
          )}

          {success && <div className="success-message">{success}</div>}
        </form>
      </div>

      <SellerPlansModal
        open={plansOpen}
        onClose={handlePlansClose}
        listingId={pendingFormData?.machineId || null}
        onPurchased={handleOnPurchased}
      />

    </Fragment>
  );
}